import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { isPushSupported } from '../lib/firebase';

const STORAGE_KEY = 'push_prompt_dismissed';
const REMINDER_DAYS = 5;

export default function PushNotificationPrompt() {
  const { isDark } = useTheme();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    checkShouldShow();
  }, []);

  const checkShouldShow = async () => {
    // Admin can globally hide the push notification UI. When the flag is
    // disabled, skip the prompt entirely — notifications can still be
    // delivered by the native wrapper without showing the UX.
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`);
      if (data && data.push_toggle_enabled === false) return;
    } catch (_) { /* fall through */ }

    // In native WebView wrappers (Natively iOS/Android), Web Push API
    // may be unavailable but native push delivery is still supported
    // via APNs/FCM through the wrapper. Force-show the prompt so the
    // user can grant the OS permission.
    const wrapped = isWrappedWebView();

    if (!wrapped) {
      // Check if push is supported (web flow)
      const supported = await isPushSupported();
      if (!supported) return;
    }

    // Check if already granted
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') return;

    // Check if user denied (don't keep asking)
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return;

    // Check localStorage for dismissal
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (!stored) {
      // First time - show immediately (new user)
      setIsNewUser(true);
      setShow(true);
      return;
    }

    try {
      const data = JSON.parse(stored);
      const dismissedAt = new Date(data.dismissedAt);
      const now = new Date();
      const daysPassed = Math.floor((now - dismissedAt) / (1000 * 60 * 60 * 24));

      // Show again after REMINDER_DAYS
      if (daysPassed >= REMINDER_DAYS) {
        setIsNewUser(false);
        setShow(true);
      }
    } catch {
      // Invalid stored data, show prompt
      setShow(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      dismissedAt: new Date().toISOString()
    }));
    setShow(false);
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      // OneSignal-unified flow — works for Capacitor native (iOS/Android),
      // WebView wrappers (Natively/TWA), and PWA browsers. The wrapper
      // chooses the underlying SDK automatically.
      const sessionToken = localStorage.getItem('session_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const headers = {
        'Content-Type': 'application/json',
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      };

      const {
        initOneSignal,
        requestOneSignalPermission,
        getOneSignalPlayerId,
      } = await import('../lib/onesignal');

      await initOneSignal();
      const granted = await requestOneSignalPermission();
      if (!granted) { handleDismiss(); return; }

      // Poll for player_id
      let playerId = null;
      for (let i = 0; i < 8; i += 1) {
        playerId = await getOneSignalPlayerId();
        if (playerId) break;
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (!playerId) {
        alert(
          'Notifiche autorizzate ma OneSignal non ha restituito un player_id. ' +
          'Chiudi e riapri l\'app, poi riprova dal Profilo.'
        );
        handleDismiss();
        return;
      }

      const { Capacitor } = await import('@capacitor/core');
      const platform = Capacitor.getPlatform();
      localStorage.setItem('onesignal_player_id', playerId);
      await fetch(`${apiUrl}/api/notifications/onesignal-player`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ player_id: playerId, platform }),
      });
      localStorage.removeItem(STORAGE_KEY);
      setShow(false);
    } catch (err) {
      console.error('Push permission error:', err);
      alert('Errore notifiche: ' + (err?.message || 'sconosciuto'));
      handleDismiss();
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      style={{ zIndex: 9999 }}
      onClick={handleDismiss}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'} animate-scale-in`}
        onClick={e => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E8334A] to-[#F59E0B] flex items-center justify-center mx-auto mb-4">
          <Bell className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h3 className={`font-display text-xl mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {isNewUser ? 'Benvenuto! 🎉' : 'Attiva le notifiche'}
        </h3>

        {/* Description */}
        <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          {isNewUser 
            ? 'Attiva le notifiche push per ricevere promemoria giornalieri, aggiornamenti sulle challenge e motivazione dalla community!'
            : 'Non perderti gli aggiornamenti! Attiva le notifiche per restare connesso con la community e ricevere i promemoria.'}
        </p>

        {/* Features */}
        <div className={`space-y-2 mb-6 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          <div className="flex items-center gap-2">
            <span className="text-lg">⏰</span>
            <span>Promemoria peso giornaliero</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🏆</span>
            <span>Aggiornamenti sulle challenge</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">💬</span>
            <span>Reazioni e commenti ai tuoi post</span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button 
            onClick={handleDismiss}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Più tardi
          </button>
          <button 
            onClick={handleEnable}
            disabled={loading}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Bell className="w-4 h-4" />
                Attiva
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
